import React, { useState, useEffect } from 'react';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './config/firebase';
import Header from './components/Header';
import CreditsBanner from './components/CreditsBanner';
import KeyPhotoSection from './components/KeyPhotoSection';
import VideoSection from './components/VideoSection';
import { FormData } from './types';
import './styles/App.css';

function App() {
  const [authInitialized, setAuthInitialized] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [credits, setCredits] = useState(10); // Default credits
  const [isLoading, setIsLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    setSignInError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      setSignInError('Sign-in failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };
  
  const [formData, setFormData] = useState<FormData>({
    // Key Photo fields
    aspectRatio: '1:1',
    platform: 'Etsy Store',
    photoType: '',
    jewelryItem: '',
    styleAesthetic: '',
    customStyle: '',
    mainMaterial: '',
    modelType: 'Diverse',
    lightingStyle: '',
    customLighting: '',
    backgroundSetting: '',
    eventType: 'None',
    customEvent: '',
    keyPhotoOtherDetails: '',
    
    // Frame 1 fields
    frame1ShotType: '',
    frame1Action: '',
    frame1OtherDetails: '',
    
    // Frame 2 fields
    frame2ShotType: '',
    frame2AngleZoom: '',
    frame2ModelPose: '',
    frame2Background: '',
    frame2OtherDetails: '',
    
    // Video fields
    cameraAngle: '',
    cameraMovement: '',
    motionSpeed: '',
    focusPoint: '',
    videoOtherDetails: '',
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.providerData.some(p => p.providerId === 'google.com')) {
        console.log('Google user signed in:', user.uid);
        setUserId(user.uid);
      } else if (user) {
        // Non-Google user — sign them out, require Google
        signOut(auth);
        setUserId(null);
      } else {
        setUserId(null);
      }
      setAuthInitialized(true);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBuyCredits = () => {
    // In a real implementation, this would open a payment modal
    alert('Payment integration coming soon! For now, this is a demo with unlimited usage.');
  };

  if (!authInitialized) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Initializing...</p>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="signin-page">
        <div className="signin-card">
          <div className="signin-logo">
            <img src="/app/favicon.svg" alt="JewelryLove" style={{ width: 56, marginBottom: 12 }} />
            <h1 className="signin-title">JewelryLove</h1>
            <p className="signin-subtitle">AI Jewelry Image &amp; Video Creator</p>
          </div>
          <button
            className="google-signin-btn"
            onClick={handleGoogleSignIn}
            disabled={signingIn}
          >
            <svg width="20" height="20" viewBox="0 0 48 48" style={{ marginRight: 10 }}>
              <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.5 33.4 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 20-8 20-20 0-1.3-.1-2.7-.4-4z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.5 18.9 12 24 12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.8-1.9 13.4-5l-6.2-5.2C29.4 35.6 26.8 36 24 36c-5.2 0-9.5-2.5-11.3-6.5l-6.6 4.8C9.7 39.7 16.3 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.4-2.5 4.4-4.6 5.8l6.2 5.2C40.8 35.6 44 30.2 44 24c0-1.3-.1-2.7-.4-4z"/>
            </svg>
            {signingIn ? 'Signing in...' : 'Continue with Google'}
          </button>
          {signInError && <p className="signin-error">{signInError}</p>}
          <p className="signin-note">Only Google accounts are accepted.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header />
      <CreditsBanner 
        credits={credits} 
        isLoading={isLoading} 
        onBuyPress={handleBuyCredits}
      />
      
      <main className="main-content">
        <div className="container">
          <div className="intro-section">
            <h2 className="intro-title">Welcome to Jewelry Prompt Builder</h2>
            <p className="intro-text">
              Generate professional AI prompts for your jewelry photography and videos. 
              Perfect for Etsy listings, Instagram posts, YouTube content, and Facebook ads.
            </p>
          </div>

          <KeyPhotoSection formData={formData} onChange={handleFormChange} />
          <VideoSection formData={formData} onChange={handleFormChange} />
          
          <div className="footer-note">
            <p>💡 <strong>Tip:</strong> Use the generated prompts with AI image generators like Midjourney, DALL-E, or Stable Diffusion for best results.</p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;





